import React, { useState, useEffect } from 'react';
import '../styles/AdminReservations.css';
import '../styles/Partners.css';

export function AdminReservationsContent({ onStatsChange }) {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [modal, setModal] = useState({ show: false, title: '', message: '', type: 'warning', onConfirm: null });
  const [successModal, setSuccessModal] = useState({ show: false, title: '', message: '' });

  const isSuperAdmin = localStorage.getItem('is_superadmin') === 'true';
  const canManage = isSuperAdmin || localStorage.getItem('can_manage_reservations') === 'true';

  const closeModal = () => setModal(m => ({ ...m, show: false, onConfirm: null }));
  const confirmModal = (title, message, onConfirm) =>
    setModal({ show: true, type: 'warning', title, message, onConfirm });

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/reservations/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/admin/login';
        return;
      }

      if (!response.ok) {
        throw new Error("Erreur lors de la récupération des réservations");
      }
      const data = await response.json();
      setReservations(data);
    } catch (err) {
      setError(err.message === 'Failed to fetch' ? "Impossible de se connecter au serveur (Backend arrêté ?)" : err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  const handleDelete = async (id) => {
    confirmModal(
      'Supprimer la réservation',
      'Cette réservation sera définitivement supprimée. Confirmer ?',
      async () => {
        try {
          const token = localStorage.getItem('access_token');
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/reservations/${id}/`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            setReservations((prev) => prev.filter((res) => res.id !== id));
            if (onStatsChange) onStatsChange();
            setSuccessModal({ show: true, title: 'Supprimée', message: 'La réservation a été supprimée avec succès.' });
          } else {
            alert("Erreur lors de la suppression de la réservation.");
          }
        } catch (err) {
          console.error(err);
          alert("Erreur de connexion.");
        }
      }
    );
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/reservations/${id}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ statut: newStatus })
      });

      if (response.ok) {
        const updated = await response.json();
        setReservations((prev) =>
          prev.map((res) => (res.id === id ? updated : res))
        );
        if (onStatsChange) onStatsChange();
      } else {
        alert("Erreur lors de la modification du statut.");
      }
    } catch (err) {
      console.error(err);
      alert("Erreur de connexion.");
    }
  };

  const calculatePrice = (nb, start, end) => {
    if (!start || !end) return "N/A";
    const d1 = new Date(start);
    const d2 = new Date(end);
    let days = Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24));
    if (days <= 0) days = 1; // Minimum 1 day

    let lodging = 100;
    let food = 150;
    let activity = 50;

    if (nb >= 15) {
      lodging = 75;
      food = 125;
    } else if (nb >= 8) {
      lodging = 75;
      food = 150;
    }

    const minPrice = days * nb * lodging;
    const maxPrice = days * nb * (lodging + food + activity);

    return `${minPrice} DH - ${maxPrice} DH`;
  };

  if (loading) return <div style={{padding: '2rem'}}>Chargement des réservations...</div>;
  if (error) return <div style={{padding: '2rem', color: 'red'}}>{error}</div>;

  return (
    <div className="admin-reservations-container">
      {/* Modal de confirmation */}
      {modal.show && (
        <div className="tiwizi-modal-overlay" onClick={closeModal}>
          <div className="tiwizi-modal-card tiwizi-details-modal" onClick={e => e.stopPropagation()}>
            <div className="tiwizi-modal-icon warning">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3>{modal.title}</h3>
            <p>{modal.message}</p>
            <div className="tiwizi-modal-actions">
              <button className="tiwizi-btn-cancel" onClick={closeModal}>Annuler</button>
              <button className="tiwizi-btn-confirm" onClick={() => { modal.onConfirm(); closeModal(); }}>Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de succès suppression */}
      {successModal.show && (
        <div className="tiwizi-modal-overlay" onClick={() => setSuccessModal({ show: false, title: '', message: '' })}>
          <div className="tiwizi-modal-card tiwizi-details-modal" onClick={e => e.stopPropagation()} style={{ textAlign: 'center', maxWidth: '420px' }}>
            <div className="tiwizi-modal-icon success" style={{ margin: '0 auto 16px' }}>
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3 style={{ marginBottom: '8px' }}>{successModal.title}</h3>
            <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '12px 0' }} />
            <p style={{ color: '#666', marginBottom: '20px' }}>{successModal.message}</p>
            <button
              onClick={() => setSuccessModal({ show: false, title: '', message: '' })}
              style={{
                background: '#1a1a1a',
                color: 'white',
                border: 'none',
                borderRadius: '30px',
                padding: '12px 40px',
                fontWeight: '700',
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              Fermer
            </button>
          </div>
        </div>
      )}


      <div className="section-title-group" style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
          Réservations
          <span className="count-badge dark" style={{ marginLeft: '10px', background: '#f5c50a', color: '#1a1a1a', borderRadius: '50px', padding: '2px 12px', fontSize: '0.85rem', fontWeight: '700', display: 'inline-block', verticalAlign: 'middle' }}>{reservations.length}</span>
        </h3>
        <div className="projets-view-toggle">
          <button type="button" className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Vue Liste">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
          </button>
          <button type="button" className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Vue Grille">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
            </svg>
          </button>
        </div>
      </div>

      {viewMode === 'grid' && (
      <div className="res-grid">
        {reservations.map(res => (          <div className="res-card" key={res.id}>
            <div className="res-card-top">
              {canManage ? (
                <select
                  value={res.statut}
                  onChange={(e) => handleStatusChange(res.id, e.target.value)}
                  className={`res-status-select ${res.statut}`}
                >
                  <option value="attente">En attente</option>
                  <option value="confirme">Confirmé</option>
                  <option value="realise">Réalisé</option>
                  <option value="annule">Annulé</option>
                </select>
              ) : (
                <span className={`res-badge ${res.statut}`}>
                  {res.statut === 'attente' ? 'En attente' : res.statut === 'confirme' ? 'Confirmé' : res.statut === 'realise' ? 'Réalisé' : 'Annulé'}
                </span>
              )}
              {canManage && (
                <button onClick={() => handleDelete(res.id)} className="res-delete-btn" title="Supprimer">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                </button>
              )}
            </div>
            
            <h4 className="res-client-name">{res.nom}</h4>
            <div className="res-client-contact">
              <div className="res-contact-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                {res.email}
              </div>
              <div className="res-contact-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                {res.telephone}
              </div>
            </div>

            <div className="res-divider"></div>

            <div className="res-details" style={{ gridTemplateColumns: '1fr' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div className="res-detail-box">
                  <span className="res-detail-label">Personnes</span>
                  <span className="res-detail-value">{res.nb_personnes} pers.</span>
                </div>
                <div className="res-detail-box" style={{ textAlign: 'right' }}>
                  <span className="res-detail-label">Période</span>
                  <span className="res-detail-value">{res.date_arrivee} <br/> au {res.date_depart}</span>
                </div>
              </div>
              <div className="res-detail-box" style={{ background: 'rgba(250, 205, 22, 0.15)', padding: '0.75rem', borderRadius: '8px', marginTop: '0.5rem', border: '1px solid rgba(250, 205, 22, 0.3)' }}>
                <span className="res-detail-label" style={{ color: '#7a6200' }}>Prix estimé (Hébergement seul → Complet)</span>
                <span className="res-detail-value" style={{ fontSize: '1.1rem', color: '#1a1a1a', fontWeight: '700' }}>
                  {calculatePrice(res.nb_personnes, res.date_arrivee, res.date_depart)}
                </span>
              </div>
            </div>

            {res.message && (
              <div className="res-message">
                "{res.message}"
              </div>
            )}
          </div>
        ))}
        
        {reservations.length === 0 && (
          <div className="res-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
            <p>Aucune réservation pour le moment.<br/>Les nouvelles demandes apparaîtront ici.</p>
          </div>
        )}
      </div>
      )}

      {viewMode === 'list' && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Personnes</th>
                <th>Arrivée</th>
                <th>Départ</th>
                <th>Prix estimé</th>
                <th>Statut</th>
                {canManage && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {reservations.length === 0 ? (
                <tr><td colSpan={canManage ? 9 : 8} className="empty-table">Aucune réservation pour le moment.</td></tr>
              ) : reservations.map(res => (
                <tr key={res.id}>
                  <td><strong>{res.nom}</strong></td>
                  <td>{res.email}</td>
                  <td>{res.telephone}</td>
                  <td>{res.nb_personnes}</td>
                  <td>{res.date_arrivee}</td>
                  <td>{res.date_depart}</td>
                  <td style={{ fontWeight: '700', color: '#1a1a1a', whiteSpace: 'nowrap' }}>{calculatePrice(res.nb_personnes, res.date_arrivee, res.date_depart)}</td>
                  <td>
                    {canManage ? (
                      <select
                        value={res.statut}
                        onChange={(e) => handleStatusChange(res.id, e.target.value)}
                        className={`res-status-select ${res.statut}`}
                        style={{ padding: '4px 28px 4px 8px', borderRadius: '20px', fontSize: '0.85rem' }}
                      >
                        <option value="attente">En attente</option>
                        <option value="confirme">Confirmé</option>
                        <option value="realise">Réalisé</option>
                        <option value="annule">Annulé</option>
                      </select>
                    ) : (
                      <span className={`res-badge ${res.statut}`}>
                        {res.statut === 'attente' ? 'En attente' : res.statut === 'confirme' ? 'Confirmé' : res.statut === 'realise' ? 'Réalisé' : 'Annulé'}
                      </span>
                    )}
                  </td>
                  {canManage && (
                    <td>
                      <button className="icon-btn" title="Supprimer" style={{ color: '#e74c3c' }} onClick={() => handleDelete(res.id)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminReservations({ onStatsChange }) {
  return <AdminReservationsContent onStatsChange={onStatsChange} />;
}
